import React, { useState } from 'react';
import { Calendar, Users, Rocket, FileText, MapPin, Clock, Edit, Trash2, Plus, Search, ChevronDown } from 'lucide-react';

const EventManagementDashboard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [statusFilter, setStatusFilter] = useState('All Status');

  const events = [
    {
      id: 1,
      title: "Team Meeting",
      type: "MEETING",
      date: "8/28/2025",
      time: "09:53 AM",
      duration: "1h",
      location: "Conference Room A",
      description: "Weekly team sync and project updates",
      icon: Users,
      color: "bg-blue-100 text-blue-600"
    },
    {
      id: 2,
      title: "Product Launch",
      type: "EVENT",
      date: "8/30/2025",
      time: "09:53 AM",
      duration: "4h",
      location: "Main Auditorium",
      description: "Launch of our new product line",
      icon: Rocket,
      color: "bg-orange-100 text-orange-600"
    },
    {
      id: 3,
      title: "Doctor Appointment",
      type: "APPOINTMENT",
      date: "9/3/2025",
      time: "09:53 AM",
      duration: "1h",
      location: "Medical Center",
      description: "Annual check-up",
      icon: FileText,
      color: "bg-purple-100 text-purple-600"
    }
  ];

  const upcomingEvents = events.filter(event => new Date(event.date) >= new Date());
  const pastEvents = events.filter(event => new Date(event.date) < new Date());
  const thisWeekEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    const today = new Date();
    const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    return eventDate >= today && eventDate <= weekFromNow;
  });

  const StatCard = ({ title, count, bgColor = "bg-blue-50" }) => (
    <div className={`${bgColor} rounded-lg p-6 text-center`}>
      <div className="text-3xl font-bold text-blue-500 mb-2">{count}</div>
      <div className="text-sm text-gray-600 uppercase tracking-wide">{title}</div>
    </div>
  );

  const EventCard = ({ event, showActions = true }) => {
    const IconComponent = event.icon;
    
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${event.color}`}>
              <IconComponent className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{event.title}</h3>
              <span className={`inline-block px-2 py-1 text-xs font-medium rounded uppercase tracking-wide ${
                event.type === 'MEETING' ? 'bg-blue-100 text-blue-800' :
                event.type === 'EVENT' ? 'bg-orange-100 text-orange-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {event.type}
              </span>
            </div>
          </div>
        </div>
        
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-red-500" />
            <span>{event.date} at {event.time}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-red-500" />
            <span>Duration: {event.duration}</span>
          </div>
          <div className="flex items-center space-x-2">
            <MapPin className="w-4 h-4 text-orange-500" />
            <span>{event.location}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Edit className="w-4 h-4 text-orange-500" />
            <span>{event.description}</span>
          </div>
        </div>
        
        {showActions && (
          <div className="flex space-x-2 mt-4">
            <button className="flex items-center space-x-1 bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm transition-colors">
              <Edit className="w-3 h-3" />
              <span>Edit</span>
            </button>
            <button className="flex items-center space-x-1 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors">
              <Trash2 className="w-3 h-3" />
              <span>Cancel</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl ">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Calendar className="w-8 h-8 text-red-500" />
            <h1 className="text-3xl font-bold text-gray-900">Event Management</h1>
          </div>
         
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard title="Total Events" count={events.length} />
          <StatCard title="Upcoming" count={upcomingEvents.length} />
          <StatCard title="Past Events" count={pastEvents.length} />
          <StatCard title="This Week" count={thisWeekEvents.length} />
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex space-x-3">
              <div className="relative">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>All Types</option>
                  <option>MEETING</option>
                  <option>EVENT</option>
                  <option>APPOINTMENT</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>All Status</option>
                  <option>Upcoming</option>
                  <option>Past</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
              <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                <Plus className="w-4 h-4" />
                <span>Add Event</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Events Grid */}
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {events.slice(0, 2).map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
            <div className="grid grid-cols-1 gap-6">
              {events.slice(2).map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>

          {/* Upcoming Events Sidebar */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center space-x-2 mb-4">
              <Calendar className="w-5 h-5 text-red-500" />
              <h3 className="font-semibold text-gray-900">Upcoming Events</h3>
            </div>
            <div className="space-y-4">
              {upcomingEvents.map((event) => (
                <div key={event.id} className="border-l-4 border-blue-500 pl-3">
                  <div className="flex items-center space-x-2 text-sm">
                    <div className={`p-1 rounded ${event.color}`}>
                      <event.icon className="w-3 h-3" />
                    </div>
                    <span className="font-medium text-gray-900">{event.title}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{event.date} at {event.time}</div>
                </div>
              ))}
              <div className="border-l-4 border-gray-300 pl-3">
                <div className="flex items-center space-x-2 text-sm">
                  <div className="p-1 rounded bg-gray-100 text-gray-600">
                    <FileText className="w-3 h-3" />
                  </div>
                  <span className="font-medium text-gray-900">Doctor Appointment</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">9/3/2025 at 09:53 AM</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventManagementDashboard;